import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SaveMerchantTransactionRequest } from '../../types';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    TextField,
    MenuItem,
    InputAdornment,
    Tooltip,
    IconButton,
} from '@mui/material';
import { apiService } from '../../services/api';
import {
    DatePicker,
    DateValidationError,
    LocalizationProvider,
    PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { PickerValue } from '@mui/x-date-pickers/internals';
import { useToast } from '@/providers/ToastContext';
import { Controller, useForm } from 'react-hook-form';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';

interface FormData {
    grossSales: string;
    interchange: string;
    reservefee: string;
    sstgfee: string;
    transactionCount: string;
    transactionAmount: string;
    refundCount: string;
    refundAmount: string;
    totalrefund: string;
    chargebackCount: string;
    chargebackAmount: string;
    chargebackRate: string;
    chargebackfee: string;
    wireCount: string;
    wireAmount: string;
    wireFeeAmount: string;
    achCount: string;
    achAmount: string;
    achFeeAmount: string;
    achfeeamount: string;
}

const MerchantTransactions: React.FC = () => {
    const { id: merchantId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // const [formData, setFormData] = useState<FormData>({
    //     grossSales: '',
    //     interchange: '0.00',
    //     reservefee: '0.00',
    //     sstgfee: '0.00',
    //     transactionCount: '',
    //     transactionAmount: '',
    //     refundCount: '',
    //     refundAmount: '',
    //     totalrefund: '0.00',
    //     chargebackCount: '',
    //     chargebackAmount: '',
    //     chargebackRate: '',
    //     chargebackfee: '0.00',
    //     wireCount: '',
    //     wireAmount: '0.00',
    //     wireFeeAmount: '0.00',
    //     achCount: '',
    //     achAmount: '0.00',
    //     achFeeAmount: '0.00',
    //     achfeeamount: '0.00',
    // });

    // const formDataRef = useRef<FormData>(formData);
    // const { showToast } = useToast();
    // useEffect(() => {
    //     formDataRef.current = formData;
    // }, [formData]);

    // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const { name, value } = e.target;

    //     // Update the form data with the new value
    //     setFormData(prev => ({
    //         ...prev,
    //         [name]: value,
    //     }));
    // };

    const [charges, setCharges] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedPaymentType, setSelectedPaymentType] = useState<
        'wire' | 'ach'
    >('wire');
    const [totalCost, setTotalCost] = useState<number | null>(null);
    const [netPayout, setNetPayout] = useState<number | null>(null);
    const { showToast } = useToast();

    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(
        dayjs()
    );
    const [dayName, setDayName] = useState<string>('');
    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        defaultValues: {
            grossSales: '',
            interchange: '0.00',
            reservefee: '0.00',
            sstgfee: '0.00',
            transactionCount: '',
            transactionAmount: '',
            refundCount: '',
            refundAmount: '',
            totalrefund: '0.00',
            chargebackCount: '',
            chargebackAmount: '',
            chargebackRate: '0.0',
            chargebackfee: '0.00',
            wireCount: '',
            wireAmount: '0.00',
            wireFeeAmount: '0.00',
            achCount: '',
            achAmount: '0.00',
            achFeeAmount: '0.00',
            achfeeamount: '0.00',
        },
    });
    const watchedValues = watch();
    // Add this handler function
    const handleDateChange = (
        value: PickerValue | null,
        context: PickerChangeHandlerContext<DateValidationError>
    ) => {
        if (value) {
            let date = dayjs(value.toISOString());
            setSelectedDate(date);
            // Get day name (e.g., "Monday", "Tuesday")
            const dayName = date.format('dddd');
            setDayName(dayName);

            // Also update the day number (0-6) in your form data if needed
            const dayNumber = date.day(); // 0 for Sunday, 6 for Saturday
            // You can store this if your API needs it
        } else {
            setDayName('');
        }
    };

    // Fetch charges on mount and auto-fill form
    useEffect(() => {
        const fetchCharges = async () => {
            try {
                const res = await apiService.getCharges();
                console.log('getCharges', res);
                if (res.success && res.data?.charges) {
                    const chargesData = res.data.charges;
                    setCharges(chargesData);

                    // Set wire amount, ACH amount, and other charges if found
                    const wireCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'Wr Amount'
                    );
                    const achCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'Ach Amount'
                    );
                    const refundCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'RF RATE'
                    );
                    const transactionCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'TC'
                    );
                    const chargebackCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'CB'
                    );
                    const icCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'IC'
                    );
                    const reserveCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'RS'
                    );
                    const sstgCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'SSTG'
                    );

                    // Prepare form updates
                    const formUpdates: Partial<FormData> = {};

                    // Payment related charges
                    if (wireCharge)
                        formUpdates.wireAmount = wireCharge.value.toString();
                    if (achCharge)
                        formUpdates.achAmount = achCharge.value.toString();

                    // Transaction related charges
                    if (transactionCharge) {
                        formUpdates.transactionAmount =
                            transactionCharge.value.toString();
                    }

                    // Fee rates
                    if (refundCharge)
                        formUpdates.chargebackRate =
                            refundCharge.value.toString();
                    if (chargebackCharge)
                        formUpdates.chargebackRate =
                            chargebackCharge.value.toString();
                    if (icCharge)
                        formUpdates.interchange = icCharge.value.toString();
                    if (reserveCharge)
                        formUpdates.reservefee = reserveCharge.value.toString();
                    if (sstgCharge)
                        formUpdates.sstgfee = sstgCharge.value.toString();

                    // Update form with auto-filled values if any were found
                    if (Object.keys(formUpdates).length > 0) {
                        // setFormData(prev => ({
                        //     ...prev,
                        //     ...formUpdates,
                        // }));
                        Object.entries(formUpdates).forEach(([key, value]) => {
                            setValue(key as keyof FormData, value);
                        });
                    }
                }
            } catch (err) {
                console.error('Error fetching charges:', err);
            }
        };
        fetchCharges();
    }, []);

    // IC / Reserve / SSTG
    useEffect(() => {
        let cancelled = false;

        const calculateFees = async () => {
            const gross = Number(watchedValues.grossSales);

            if (!gross || isNaN(gross) || gross <= 0) {
                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     interchange: '0.00',
                    //     reservefee: '0.00',
                    //     sstgfee: '0.00',
                    // }));
                    setValue('interchange', '0.00');
                    setValue('reservefee', '0.00');
                    setValue('sstgfee', '0.00');
                }
                return;
            }

            if (charges.length === 0) return;

            const ic = charges.find(c => c.chargeCode === 'IC');
            const reserve = charges.find(c => c.chargeCode === 'RS');
            const sstg = charges.find(c => c.chargeCode === 'SSTG');
            if (!ic || !reserve || !sstg) return;

            if (!cancelled) setLoading(true);
            try {
                const [icRes, reserveRes, sstgRes] = await Promise.all([
                    apiService.calculateIC({
                        icRate: ic.value,
                        grossSales: gross,
                    }),
                    apiService.calculateReserve({
                        reserveRate: reserve.value,
                        grossSales: gross,
                    }),
                    apiService.calculateSSTG({
                        sstgRate: sstg.value,
                        grossSales: gross,
                    }),
                ]);

                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     interchange:
                    //         icRes?.data?.interchange !== undefined &&
                    //             icRes.data.interchange !== null
                    //             ? Number(icRes.data.interchange).toFixed(2)
                    //             : '0.00',
                    //     reservefee:
                    //         reserveRes?.data?.reservefee !== undefined &&
                    //             reserveRes.data.reservefee !== null
                    //             ? Number(reserveRes.data.reservefee).toFixed(2)
                    //             : '0.00',
                    //     sstgfee:
                    //         sstgRes?.data?.sstgfee !== undefined &&
                    //             sstgRes.data.sstgfee !== null
                    //             ? Number(sstgRes.data.sstgfee).toFixed(2)
                    //             : '0.00',
                    // }));
                    setValue(
                        'interchange',
                        icRes?.data?.interchange !== undefined &&
                            icRes.data.interchange !== null
                            ? Number(icRes.data.interchange).toFixed(2)
                            : '0.00'
                    );
                    setValue(
                        'reservefee',
                        reserveRes?.data?.reservefee !== undefined &&
                            reserveRes.data.reservefee !== null
                            ? Number(reserveRes.data.reservefee).toFixed(2)
                            : '0.00'
                    );
                    setValue(
                        'sstgfee',
                        sstgRes?.data?.sstgfee !== undefined &&
                            sstgRes.data.sstgfee !== null
                            ? Number(sstgRes.data.sstgfee).toFixed(2)
                            : '0.00'
                    );
                }
            } catch (err) {
                console.error('Error calculating fees:', err);
                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     interchange: '0.00',
                    //     reservefee: '0.00',
                    //     sstgfee: '0.00',
                    // }));
                    setValue('interchange', '0.00');
                    setValue('reservefee', '0.00');
                    setValue('sstgfee', '0.00');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        calculateFees();

        return () => {
            cancelled = true;
        };
    }, [watchedValues.grossSales, charges]);

    // Refund
    useEffect(() => {
        let cancelled = false;

        const calculateRefund = async () => {
            const refundCount = Number(watchedValues.refundCount);
            const refundAmount = Number(watchedValues.refundAmount);
            if (!refundCount || !refundAmount) return;

            const refundCharge = charges.find(c => c.chargeCode === 'RF RATE');
            if (!refundCharge) return;

            if (!cancelled) setLoading(true);
            try {
                const res = await apiService.calculateRefund({
                    refundCount,
                    refundAmount,
                    refundfee: refundCharge.value,
                });
                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     totalrefund:
                    //         res?.data?.totalrefund?.toFixed(2) || '0.00',
                    // }));
                    setValue(
                        'totalrefund',
                        res?.data?.totalrefund?.toFixed(2) || '0.00'
                    );
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        calculateRefund();

        return () => {
            cancelled = true;
        };
    }, [watchedValues.refundCount, watchedValues.refundAmount, charges]);

    // Chargeback
    useEffect(() => {
        let cancelled = false;

        const calculateChargeback = async () => {
            const count = Number(watchedValues.chargebackCount);
            const amount = Number(watchedValues.chargebackAmount);
            const rate = Number(watchedValues.chargebackRate);
            if (!count || !amount || !rate) return;

            if (!cancelled) setLoading(true);
            try {
                const res = await apiService.calculateChargeback({
                    chargebackCount: count,
                    chargebackAmount: amount,
                    chargebackFee: rate,
                });
                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     chargebackfee:
                    //         res?.data?.chargebackfee?.toFixed(2) || '0.00',
                    // }));
                    setValue(
                        'chargebackfee',
                        res?.data?.chargebackfee?.toFixed(2) || '0.00'
                    );
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        calculateChargeback();

        return () => {
            cancelled = true;
        };
    }, [
        watchedValues.chargebackCount,
        watchedValues.chargebackAmount,
        watchedValues.chargebackRate,
    ]);

    useEffect(() => {
        if (selectedDate) {
            let date = dayjs(selectedDate);
            const dayName = date.format('dddd');
            setDayName(dayName);
        }
    }, []);

    // Wire Fee
    useEffect(() => {
        if (selectedPaymentType !== 'wire') return;
        let cancelled = false;

        const calculateWire = async () => {
            const count = Number(watchedValues.wireCount);
            if (!count) return;

            const wireCharge = charges.find(c => c.chargeCode === 'Wr Amount');
            if (!wireCharge) return;

            if (!cancelled) setLoading(true);
            try {
                const res = await apiService.calculateWireFee({
                    wireCount: count,
                    wireAmount: wireCharge.value,
                });
                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     wireFeeAmount:
                    //         res?.data?.wirefeeamount?.toFixed(2) || '0.00',
                    //     wireAmount: wireCharge.value.toString(),
                    // }));
                    setValue(
                        'wireFeeAmount',
                        res?.data?.wirefeeamount?.toFixed(2) || '0.00'
                    );
                    setValue('wireAmount', wireCharge.value.toString());
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        calculateWire();

        return () => {
            cancelled = true;
        };
    }, [watchedValues.wireCount, charges, selectedPaymentType]);

    // ACH Fee
    useEffect(() => {
        if (selectedPaymentType !== 'ach') return;
        let cancelled = false;

        const calculateAch = async () => {
            const count = Number(watchedValues.achCount);
            if (!count) return;

            const achCharge = charges.find(c => c.chargeCode === 'Ach Amount');
            if (!achCharge) return;

            if (!cancelled) setLoading(true);
            try {
                const res = await apiService.calculateAchFee({
                    achCount: count,
                    achAmount: achCharge.value,
                });
                if (!cancelled) {
                    // setFormData(prev => ({
                    //     ...prev,
                    //     achFeeAmount:
                    //         res?.data?.achfeeamount?.toFixed(2) || '0.00',
                    //     achAmount: achCharge.value.toString(),
                    // }));
                    setValue(
                        'achFeeAmount',
                        res?.data?.achfeeamount?.toFixed(2) || '0.00'
                    );
                    setValue('achAmount', achCharge.value.toString());
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        calculateAch();

        return () => {
            cancelled = true;
        };
    }, [watchedValues.achCount, charges, selectedPaymentType]);

    // Calculate total cost and net payout
    useEffect(() => {
        if (watchedValues.grossSales) {
            const grossSales = Number(watchedValues.grossSales);
            const interchange = Number(watchedValues.interchange) || 0;
            const reserveFee = Number(watchedValues.reservefee) || 0;
            const sstgFee = Number(watchedValues.sstgfee) || 0;
            const refundFee = Number(watchedValues.totalrefund) || 0;
            const chargebackFee = Number(watchedValues.chargebackfee) || 0;
            const wireFee = Number(watchedValues.wireFeeAmount) || 0;
            const achFee = Number(watchedValues.achFeeAmount) || 0;
            const transactionFee =
                Number(watchedValues.transactionAmount) *
                (Number(watchedValues.transactionCount) || 0);

            const totalCost =
                interchange +
                reserveFee +
                sstgFee +
                refundFee +
                chargebackFee +
                (selectedPaymentType === 'wire' ? wireFee : achFee) +
                transactionFee;

            const netPayout = grossSales - totalCost;

            setTotalCost(totalCost);
            setNetPayout(netPayout);
        }
    }, [
        watchedValues.grossSales,
        watchedValues.interchange,
        watchedValues.reservefee,
        watchedValues.sstgfee,
        watchedValues.totalrefund,
        watchedValues.chargebackfee,
        watchedValues.wireFeeAmount,
        watchedValues.achFeeAmount,
        watchedValues.transactionAmount,
        watchedValues.transactionCount,
        selectedPaymentType,
    ]);

    // Save calculation
    const onSubmit = async (data: FormData) => {
        if (!merchantId) {
            console.error('Merchant ID is missing from URL');
            showToast(
                'Error: Could not determine merchant. Please try again.',
                'error'
            );
            return;
        }

        // Validate that a date is selected
        if (!selectedDate) {
            showToast('Please select a date', 'error');
            return;
        }

        try {
            setLoading(true);
            const payload: SaveMerchantTransactionRequest = {
                day: dayName,
                date: selectedDate.format('YYYY-MM-DD HH:mm:ss.SSS[+00]'),
                sales: Number(data.grossSales) || 0,
                chargebackCount: Number(data.chargebackCount) || 0,
                chargebackAmount: Number(data.chargebackAmount) || 0,
                chargebackFee: Number(data.chargebackRate) || 0,
                chargebackFeeAmount: Number(data.chargebackfee) || 0,
                icRate: charges.find(c => c.chargeCode === 'IC')?.value || 0,
                icAmount: Number(data.interchange) || 0,
                refundCount: Number(data.refundCount) || 0,
                refundAmount: Number(data.refundAmount) || 0,
                refundFee:
                    charges.find(c => c.chargeCode === 'RF RATE')?.value || 0,
                refundFeeAmount: Number(data.totalrefund) || 0,
                sstgCharge:
                    charges.find(c => c.chargeCode === 'SSTG')?.value || 0,
                sstgFee: Number(data.sstgfee) || 0,
                reserveCharge:
                    charges.find(c => c.chargeCode === 'RS')?.value || 0,
                reserveFee: Number(data.reservefee) || 0,
                wirecount:
                    selectedPaymentType === 'wire'
                        ? Number(data.wireCount) || 0
                        : 0,
                achcount:
                    selectedPaymentType === 'ach'
                        ? Number(data.achCount) || 0
                        : 0,
                achAmount: Number(data.achAmount) || 0,
                payoutServiceCharge: 0, // Can be calculated if needed
                totalCost: totalCost || 0,
                paid_amount: 0, // Can be updated when payment is made
                netPayout: netPayout || 0,
                transactions: Number(data.transactionCount) || 0,
                transactionCharge:
                    charges.find(c => c.chargeCode === 'TC')?.value || 0,
                transactionFee:
                    Number(data.transactionAmount) *
                    (Number(data.transactionCount) || 0),
                merchantId: Number(merchantId),
                transferType: selectedPaymentType,
            };
            const response = await apiService.saveMerchantTransaction(payload);
            if (response.success) {
                showToast('Transaction saved successfully!', 'success');
                navigate(`/viewTransaction/${merchantId}`);
            } else {
                throw new Error(
                    response.message || 'Failed to save transaction'
                );
            }
        } catch (err) {
            showToast(
                `Failed to save transaction: ${err instanceof Error ? err.message : 'Unknown error'}`,
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    // --- Render UI ---
    return (
        <Box sx={{ p: { xs: 2, sm: 2 } }}>
            {/* <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                Merchant Transaction
            </Typography> */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton size="small" sx={{ mr: 1, color: 'primary.main' }}>
                    <CalculateOutlinedIcon />
                </IconButton>
                <Typography variant="h5" sx={{ mr: 1 }}>
                    Merchant Transaction
                </Typography>
            </Box>

            {/* <Box display="flex" flexDirection="column" gap={3} mt={1}> */}
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                display="flex"
                flexDirection="column"
                gap={3}
                mt={1}
            >
                {/* Gross Sales Input */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                },
                            }}
                        />
                    </LocalizationProvider>
                    <TextField
                        label="Day"
                        value={dayName}
                        InputProps={{ readOnly: true }}
                        fullWidth
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        // name="grossSales"
                        label="Gross Sales"
                        type="number"
                        {...register('grossSales', {
                            required: 'Gross Sales is required',
                        })}
                        error={!!errors.grossSales}
                        helperText={errors.grossSales?.message}
                        // value={formData.grossSales}
                        // onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        inputProps={{
                            min: 0,
                            step: '0.01',
                        }}
                    />
                </Box>

                {/* Interchange Section */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Interchange"
                            value={(() => {
                                const icCharge = [...charges]
                                    .reverse()
                                    .find(c => c.chargeCode === 'IC');
                                return icCharge
                                    ? parseFloat(icCharge.value).toFixed(2)
                                    : '0.00';
                            })()}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="IC Amount"
                            value={
                                loading
                                    ? 'Calculating...'
                                    : watchedValues.interchange || '0.00'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: loading ? (
                                    <CircularProgress size={20} />
                                ) : null,
                            }}
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Reserve Section */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Reserve Fee"
                            value={(() => {
                                const reserveCharge = [...charges]
                                    .reverse()
                                    .find(c => c.chargeCode === 'RS');
                                return reserveCharge
                                    ? parseFloat(reserveCharge.value).toFixed(2)
                                    : '0.00';
                            })()}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Reserve Amount"
                            value={
                                loading
                                    ? 'Calculating...'
                                    : watchedValues.reservefee || '0.00'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: loading ? (
                                    <CircularProgress size={20} />
                                ) : null,
                            }}
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* SSTG Section */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="SSTG Fee"
                            value={(() => {
                                const sstgCharge = [...charges]
                                    .reverse()
                                    .find(c => c.chargeCode === 'SSTG');
                                return sstgCharge
                                    ? parseFloat(sstgCharge.value).toFixed(2)
                                    : '0.00';
                            })()}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="SSTG Amount"
                            value={
                                loading
                                    ? 'Calculating...'
                                    : watchedValues.sstgfee || '0.00'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: loading ? (
                                    <CircularProgress size={20} />
                                ) : null,
                            }}
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Transaction Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Controller
                            name="transactionCount"
                            control={control}
                            rules={{
                                required: 'Transaction Count is required',
                                min: {
                                    value: 0,
                                    message: 'ACH Count must be 0 or more',
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    // name="transactionCount"
                                    label="Transaction Count"
                                    type="number"
                                    // value={formData.transactionCount}
                                    error={!!errors.transactionCount}
                                    helperText={
                                        errors.transactionCount?.message
                                    }
                                    onChange={async e => {
                                        const value = e.target.value;
                                        field.onChange(value);
                                        // setFormData(prev => ({
                                        //     ...prev,
                                        //     transactionCount: value,
                                        // }));

                                        const transactionCount =
                                            parseFloat(value);
                                        if (
                                            !isNaN(transactionCount) &&
                                            transactionCount >= 0
                                        ) {
                                            // Calculate transaction amount using the API
                                            const transactionCharge =
                                                charges.find(
                                                    c => c.chargeCode === 'TX'
                                                );
                                            if (transactionCharge) {
                                                try {
                                                    const response =
                                                        await apiService.calculateTransaction(
                                                            {
                                                                transactionRate:
                                                                    transactionCharge.value,
                                                                transactionCount:
                                                                    transactionCount,
                                                            }
                                                        );

                                                    const amount =
                                                        response?.data?.transactionfee?.toFixed(
                                                            2
                                                        ) || '0.00';
                                                    // setFormData(prev => ({
                                                    //     ...prev,
                                                    //     transactionAmount: amount,
                                                    // }));
                                                    setValue(
                                                        'transactionAmount',
                                                        amount
                                                    );
                                                } catch (error) {
                                                    console.error(
                                                        'Error calculating transaction fee:',
                                                        error
                                                    );
                                                    setValue(
                                                        'transactionAmount',
                                                        '0.00'
                                                    );

                                                    // setFormData(prev => ({
                                                    //     ...prev,
                                                    //     transactionAmount: '0.00',
                                                    // }));
                                                }
                                            }
                                        } else {
                                            //     setFormData(prev => ({
                                            //         ...prev,
                                            //         transactionAmount: '0.00',
                                            //     }));
                                            // }
                                            setValue(
                                                'transactionAmount',
                                                '0.00'
                                            );
                                        }
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    inputProps={{
                                        min: 0,
                                        step: '1',
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        {charges
                            .filter(c => c.chargeCode === 'TX')
                            .map(charge => (
                                <TextField
                                    key={charge.id}
                                    label="Transaction Rate"
                                    value={
                                        charge.value
                                            ? parseFloat(charge.value).toFixed(
                                                  2
                                              )
                                            : '0.00'
                                    }
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    variant="outlined"
                                />
                            ))}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Transaction Amount"
                            value={
                                loading
                                    ? 'Calculating...'
                                    : watchedValues.transactionAmount || '0.00'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: loading ? (
                                    <CircularProgress size={20} />
                                ) : null,
                            }}
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Refund Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            {...register('refundCount', {
                                required: 'Refund Count is required',
                            })}
                            // name="refundCount"
                            label="Refund Count"
                            type="number"
                            // value={formData.refundCount}
                            error={!!errors.refundCount}
                            helperText={errors.refundCount?.message}
                            // onChange={e => {
                            //     const value = e.target.value;
                            //     if (value === '' || /^\d+$/.test(value)) {
                            //         const newFormData = {
                            //             ...formDataRef.current,
                            //             refundCount: value,
                            //         };
                            //         formDataRef.current = newFormData;
                            //         setFormData(newFormData);
                            //     }
                            // }}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                min: 0,
                                step: '1',
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            {...register('refundAmount', {
                                required: 'Refund Amount is required',
                            })}
                            // name="refundAmount"
                            label="Actual Refund Amount"
                            type="number"
                            // value={formData.refundAmount}
                            // onChange={e => {
                            //     const value = e.target.value;
                            //     if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            //         const newFormData = {
                            //             ...formDataRef.current,
                            //             refundAmount: value,
                            //         };
                            //         formDataRef.current = newFormData;
                            //         setFormData(newFormData);
                            //     }
                            // }}
                            error={!!errors.refundAmount}
                            helperText={errors.refundAmount?.message}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                min: 0,
                                step: '0.01',
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Refund Fee Amount"
                            value={
                                loading
                                    ? 'Calculating...'
                                    : watchedValues.totalrefund || '0.00'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: loading ? (
                                    <CircularProgress size={20} />
                                ) : null,
                            }}
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Chargeback Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            {...register('chargebackCount', {
                                required: 'Chargeback Count is required',
                            })}
                            error={!!errors.chargebackCount}
                            helperText={errors.chargebackCount?.message}
                            // name="chargebackCount"
                            label="Chargeback Count"
                            type="number"
                            // value={formData.chargebackCount}
                            // onChange={e => {
                            //     const value = e.target.value;
                            //     if (value === '' || /^\d+$/.test(value)) {
                            //         const newFormData = {
                            //             ...formDataRef.current,
                            //             chargebackCount: value,
                            //         };
                            //         formDataRef.current = newFormData;
                            //         setFormData(newFormData);
                            //     }
                            // }}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                step: '1',
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            {...register('chargebackAmount', {
                                required: 'Chargeback Amount is required',
                            })}
                            // name="chargebackAmount"
                            label="Chargeback Amount"
                            type="number"
                            error={!!errors.chargebackAmount}
                            helperText={errors.chargebackAmount?.message}
                            // value={formData.chargebackAmount}
                            // onChange={e => {
                            //     const value = e.target.value;
                            //     if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            //         const newFormData = {
                            //             ...formDataRef.current,
                            //             chargebackAmount: value,
                            //         };
                            //         formDataRef.current = newFormData;
                            //         setFormData(newFormData);
                            //     }
                            // }}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                // min: 0,
                                step: '0.01',
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            {...register('chargebackRate', {
                                required: 'Chargeback Rate is required',
                            })}
                            // name="chargebackRate"
                            label="Chargeback Rate"
                            type="number"
                            error={!!errors.chargebackRate}
                            helperText={errors.chargebackRate?.message}
                            // value={formData.chargebackRate}
                            // onChange={e => {
                            //     const value = e.target.value;
                            //     if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            //         const newFormData = {
                            //             ...formDataRef.current,
                            //             chargebackRate: value,
                            //         };
                            //         formDataRef.current = newFormData;
                            //         setFormData(newFormData);
                            //     }
                            // }}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                // min: 0,
                                step: '0.01',
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            name="chargebackfee"
                            label="Chargeback Fee Amount"
                            value={
                                loading
                                    ? 'Calculating...'
                                    : watchedValues.chargebackfee || '0.00'
                            }
                            InputProps={{
                                readOnly: true,
                                endAdornment: loading ? (
                                    <CircularProgress size={20} />
                                ) : null,
                            }}
                            fullWidth
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Payment Type Selection */}
                <TextField
                    select
                    label="Payment Type"
                    value={selectedPaymentType}
                    onChange={e =>
                        setSelectedPaymentType(e.target.value as 'wire' | 'ach')
                    }
                    fullWidth
                    variant="outlined"
                >
                    <MenuItem value="wire">Wire</MenuItem>
                    <MenuItem value="ach">ACH</MenuItem>
                </TextField>

                {/* Wire Fee Section */}
                {selectedPaymentType === 'wire' ? (
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Controller
                                name="wireCount"
                                control={control}
                                rules={{
                                    required: 'Wire Count is required',
                                    min: {
                                        value: 0,
                                        message: 'Wire Count must be 0 or more',
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        name="wireCount"
                                        label="Wire Count"
                                        type="number"
                                        error={!!errors.wireCount}
                                        helperText={errors.wireCount?.message}
                                        // value={formData.wireCount}
                                        // onChange={e => {
                                        //     const value = e.target.value;
                                        //     if (value === '' || /^\d*$/.test(value)) {
                                        //         const newFormData = {
                                        //             ...formData,
                                        //             wireCount: value,
                                        //         };
                                        //         formDataRef.current = newFormData;
                                        //         setFormData(newFormData);

                                        //         // Calculate wire fee amount
                                        //         if (value) {
                                        //             const wireCharge = charges.find(
                                        //                 c =>
                                        //                     c.chargeCode === 'Wr Amount'
                                        //             );
                                        //             if (wireCharge) {
                                        //                 const wireFeeAmount = (
                                        //                     parseFloat(value) *
                                        //                     parseFloat(wireCharge.value)
                                        //                 ).toFixed(2);
                                        //                 setFormData(prev => ({
                                        //                     ...prev,
                                        //                     wireFeeAmount,
                                        //                 }));
                                        //             }
                                        //         } else {
                                        //             setFormData(prev => ({
                                        //                 ...prev,
                                        //                 wireFeeAmount: '0.00',
                                        //             }));
                                        //         }
                                        //     }
                                        // }}
                                        fullWidth
                                        variant="outlined"
                                        inputProps={{
                                            min: 0,
                                            step: '1',
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                name="wireAmount"
                                label="Wire Amount"
                                value={watchedValues.wireAmount || '0.00'}
                                InputProps={{
                                    readOnly: true,
                                }}
                                fullWidth
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                name="wireFeeAmount"
                                label="Wire Fee Amount"
                                value={
                                    loading
                                        ? 'Calculating...'
                                        : watchedValues.wireFeeAmount || '0.00'
                                }
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: loading ? (
                                        <CircularProgress size={20} />
                                    ) : null,
                                }}
                                fullWidth
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                ) : (
                    /* ACH Section */
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Controller
                                name="achCount"
                                control={control}
                                rules={{
                                    required: 'ACH Count is required',
                                    min: {
                                        value: 0,
                                        message: 'ACH Count must be 0 or more',
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        name="achCount"
                                        label="ACH Count"
                                        type="number"
                                        error={!!errors.achCount}
                                        helperText={errors.achCount?.message}
                                        // value={formData.achCount}
                                        onChange={e => {
                                            const value = e.target.value;
                                            field.onChange(value);

                                            // if (value === '' || /^\d*$/.test(value)) {
                                            //     const newFormData = {
                                            //         ...formData,
                                            //         achCount: value,
                                            //     };
                                            //     formDataRef.current = newFormData;
                                            //     setFormData(newFormData);

                                            // Calculate ACH fee amount
                                            if (value) {
                                                const achCharge = charges.find(
                                                    c =>
                                                        c.chargeCode ===
                                                        'Ach Amount'
                                                );
                                                if (achCharge) {
                                                    const achFeeAmount = (
                                                        parseFloat(value) *
                                                        parseFloat(
                                                            achCharge.value
                                                        )
                                                    ).toFixed(2);
                                                    // setFormData(prev => ({
                                                    //     ...prev,
                                                    //     achFeeAmount,
                                                    // }));
                                                }
                                            } else {
                                                // setFormData(prev => ({
                                                //     ...prev,
                                                //     achFeeAmount: '0.00',
                                                // }));
                                                setValue(
                                                    'achFeeAmount',
                                                    '0.00'
                                                );
                                            }
                                            // }
                                        }}
                                        fullWidth
                                        variant="outlined"
                                        inputProps={{
                                            min: 0,
                                            step: '1',
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                // name="achAmount"
                                label="ACH Amount"
                                value={watchedValues.achAmount || '0.00'}
                                InputProps={{
                                    readOnly: true,
                                }}
                                fullWidth
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                name="achFeeAmount"
                                label="ACH Fee Amount"
                                value={
                                    loading
                                        ? 'Calculating...'
                                        : watchedValues.achFeeAmount || '0.00'
                                }
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: loading ? (
                                        <CircularProgress size={20} />
                                    ) : null,
                                }}
                                fullWidth
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                )}

                {/* Total Cost and Net Payout */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Total Cost"
                            value={
                                totalCost !== null
                                    ? `${totalCost.toFixed(2)}`
                                    : 'Calculating...'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Net Payout"
                            value={
                                netPayout !== null
                                    ? netPayout.toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                      })
                                    : 'Calculating...'
                            }
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor:
                                        netPayout !== null && netPayout >= 0
                                            ? 'rgba(0, 200, 0, 0.1)'
                                            : 'rgba(255, 0, 0, 0.1)',
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Save Button */}
                {/* <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveCalculation}
                        disabled={loading}
                        startIcon={
                            loading ? <CircularProgress size={20} /> : null
                        }
                    >
                        {loading ? 'Saving...' : 'Save Calculation'}
                    </Button>
                </Box> */}
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}
                >
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading || isSubmitting}
                        startIcon={
                            loading || isSubmitting ? (
                                <CircularProgress size={20} />
                            ) : null
                        }
                    >
                        {loading || isSubmitting
                            ? 'Saving...'
                            : 'Save Transaction'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default MerchantTransactions;
